import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Minimal accessible radio group built on native <input type="radio"> — no extra
 * dependency (@radix-ui/react-radio-group is not installed). A RadioGroup provides
 * the shared name + current value via context; each RadioGroupItem is a real radio
 * input styled as a dot. Used by the Data Updater method picker.
 */

interface RadioGroupContextValue {
  name: string;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

let radioGroupSeq = 0;

export interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, value, defaultValue, onValueChange, disabled, name, ...props }, ref) => {
    const autoName = React.useMemo(() => `radio-group-${++radioGroupSeq}`, []);
    const [internal, setInternal] = React.useState<string | undefined>(defaultValue);
    const isControlled = value !== undefined;
    const current = isControlled ? value : internal;

    const handleChange = React.useCallback(
      (next: string) => {
        if (!isControlled) setInternal(next);
        onValueChange?.(next);
      },
      [isControlled, onValueChange],
    );

    return (
      <RadioGroupContext.Provider
        value={{ name: name ?? autoName, value: current, onValueChange: handleChange, disabled }}
      >
        <div ref={ref} role="radiogroup" className={cn("grid gap-2", className)} {...props} />
      </RadioGroupContext.Provider>
    );
  },
);
RadioGroup.displayName = "RadioGroup";

export interface RadioGroupItemProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> {
  value: string;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ className, value, disabled, id, ...props }, ref) => {
    const ctx = React.useContext(RadioGroupContext);
    const checked = ctx?.value === value;
    const isDisabled = disabled || ctx?.disabled;

    return (
      <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
        <input
          ref={ref}
          type="radio"
          id={id}
          name={ctx?.name}
          value={value}
          checked={checked}
          disabled={isDisabled}
          onChange={() => ctx?.onValueChange?.(value)}
          className={cn(
            "peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-primary shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        />
        <span className="pointer-events-none absolute hidden h-2 w-2 rounded-full bg-primary peer-checked:block" />
      </span>
    );
  },
);
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
